import React from "react"
import {MathKatex2} from "../../../../components/MathKatex"
import "./Directions.css"

/**
 * Provides basic directions for the computer-based virtual tests
 * Primarily there are two sections: R&W & Math
 *
 * @param props 
 * @returns 
 */
export default function Directions(props) {
    let {currentModuleNameKey, onClose} = props
    let directionsContent = getDirectionsContent(currentModuleNameKey, onClose)
    
    return (
        <div className="directions-container">
            <div className="directions-info">
                {directionsContent}
            </div>
            <button className="directions-close-button" onClick={onClose}><span className="sr-only">close</span></button>
        </div>
    )
}


//--- Helpers ---
/**
 * Gets the directions for the sections/modules
 * @param currentModuleNameKey 
 * @returns 
 */
function getDirectionsContent(currentModuleNameKey: string, onClose: () => void) {
    if (!currentModuleNameKey.includes("math")) {
        return <DirectionsInfoRW onClose={onClose} />
    } else {
        return <DirectionsInfoMath onClose={onClose} />
    }
}

// Reading & Writing (R&W) Directions
function DirectionsInfoRW({ onClose }: { onClose: () => void }) {
     return (
        <div className="directions-inner">
            <div className="directions-inner--box">
            <p>
                The questions in this section address a number of important reading and writing skills.
                Each question includes one or more passages, which may include a table or graph.
                Read each passage and question carefully, and then choose the best answer to the question based on the passage(s).
            </p>

            <p>
                All questions in this section are multiple-choice with four answer choices. 
                Each question has single best answer.
            </p>
            </div>
            <div className="directions-inner--footer">
                <button className="btn btn--pill btn--bg-yellow btn--txt-default" onClick={onClose}>Close</button>
            </div>
        </div>
    )
}

// Math Directions
function DirectionsInfoMath({ onClose }: { onClose: () => void }) {
     return (
        <div className="directions-inner">
            <div className="directions-inner--box">
                <p>
                    This questions in this section address a number of math skills.
                    Use of a calculator is permitted for all questions. A reference sheet, calculator, and these directions 
                    can be accessed throughout the test.
                </p>

                <p>
                    Unless otherwise indicated:
                    <ul>
                        <li>All variables and expressions represent real numbers.</li>
                        <li>Figures provided are drawn to scale.</li>
                        <li>All figures lie in a plane.</li>
                        <li>
                            <MathKatex2 
                                mixedContent="The domain of a given function `f` is the set of all real numbers `x` for which `f(x)` is a real number." 
                                classNameForMath="computer-test-math"
                            />
                        </li>
                    </ul>
                </p>

                <p>
                    For <span className="bold">multiple-choice questions</span>, solve each problem and choose the correct answer from 
                    the choices provided.
                    <br/>
                    Each multiple-choice questions has single correct answer.
                </p>

                <p>
                    For <span className="bold">student-produced response questions</span>, solve each problem and enter your answer as described below.

                    <ul>
                        <li>If you find <span className="bold">more than one correct answer</span>, enter only one answer.</li>
                        <li>You can enter up to 5 characters for a <span className="bold">positive</span> answer and up to 6 characters (including the negative sign) for a negative answer.</li>
                        <li>If your answer is a <span className="bold">fraction</span> that doesn't fit in the provided space, enter the decimal equivalent.</li>
                        <li>If your answer is a <span className="bold">decimal</span> that doesn't fit in the provided space, enter it by truncating or rounding at the fourth digit.</li>
                        <li>If your answer is a <span className="bold">mixed number</span>  (such as 3 1/2), enter it as an improper fractions (7/2) or its decimal equivalent (3.5).</li>
                        <li>Don't enter <span className="bold">symbols</span> such as a percent sign, comma, or dollar sign.</li>
                    </ul>

                    <h2 className="h4 mb-4">Examples</h2>
                    <table className="answer-table">
                        <thead>
                            <tr>
                                <th>Answer</th>
                                <th>Acceptable ways to enter answer</th>
                                <th>Unacceptable: will <span className="italics">NOT</span> receive credit</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>3.5</td>
                                <td>
                                    3.5
                                    <br/>
                                    3.50
                                    <br/>
                                    7/2
                                </td>
                                <td>
                                    31/2
                                    <br/>
                                    3 1/2
                                </td>
                            </tr>
                            <tr>
                                <td><MathKatex2 mixedContent="`\frac{2}{3}`"/></td>
                                <td>
                                    2/3
                                    <br/>
                                    .6666
                                    <br/>
                                    .6667
                                    <br/>
                                    0.6666
                                    <br/>
                                    0.6667
                                </td>
                                <td>
                                    0.66
                                    <br/>
                                    .66
                                    <br/>
                                    0.67
                                    <br/>
                                    .67
                                </td>
                            </tr>
                            <tr>
                                <td><MathKatex2 mixedContent="`-\frac{1}{3}`"/></td>
                                <td>
                                    -1/3
                                    <br/>
                                    -.3333
                                    <br/>
                                    -0.333
                                </td>
                                <td>
                                    -.33
                                    <br/>
                                    -0.33
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </p>
            </div>
            <div className="directions-inner--footer">
                <button className="btn btn--pill" onClick={onClose}>Close</button>
            </div>
        </div>
    )
}

/**
 * This similar information is used within the question section (left panel)
 */
export function DirectionsForMathInQuestion(props) {
    return (
        <div className="directions-info-in-question">
            <h1>Student-produced response directions</h1>
            <ul>
                <li>If you find <span className="bold">more than one correct answer</span>, enter only one answer.</li>
                <li>You can enter up to 5 characters for a <span className="bold">positive</span> answer and up to 6 characters (including the negative sign) for a negative answer.</li>
                <li>If your answer is a <span className="bold">fraction</span> that doesn't fit in the provided space, enter the decimal equivalent.</li>
                <li>If your answer is a <span className="bold">decimal</span> that doesn't fit in the provided space, enter it by truncating or rounding at the fourth digit.</li>
                <li>If your answer is a <span className="bold">mixed number</span>  (such as 3 1/2), enter it as an improper fractions (7/2) or its decimal equivalent (3.5).</li>
                <li>Don't enter <span className="bold">symbols</span> such as a percent sign, comma, or dollar sign.</li>
            </ul>

            <h2>Examples</h2>
            <table className="answer-table">
                <thead>
                    <tr>
                        <th>Answer</th>
                        <th>Acceptable ways to enter answer</th>
                        <th>Unacceptable: will <span className="italics">NOT</span> receive credit</th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td>3.5</td>
                        <td>
                            3.5
                            <br/>
                            3.50
                            <br/>
                            7/2
                        </td>
                        <td>
                            31/2
                            <br/>
                            3 1/2
                        </td>
                    </tr>
                    <tr>
                        <td><MathKatex2 mixedContent="`\frac{2}{3}`"/></td>
                        <td>
                            2/3
                            <br/>
                            .6666
                            <br/>
                            .6667
                            <br/>
                            0.6666
                            <br/>
                            0.6667
                        </td>
                        <td>
                            0.66
                            <br/>
                            .66
                            <br/>
                            0.67
                            <br/>
                            .67
                        </td>
                    </tr>
                    <tr>
                        <td><MathKatex2 mixedContent="`-\frac{1}{3}`"/></td>
                        <td>
                            -1/3
                            <br/>
                            -.3333
                            <br/>
                            -0.333
                        </td>
                        <td>
                            -.33
                            <br/>
                            -0.33
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    )
}